import { Toast } from '@byecode/ui'
import { ClassNode, type FlowEdge, type FlowNode } from '@lighthouse/shared'
import type { Node, ReactFlowState } from '@xyflow/react'
import { useReactFlow, useStore } from '@xyflow/react'
import { useCallback } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useUpdateEffect } from 'react-use'

import type { FlowModalType } from '@/contexts/FlowContext'
import { useFlow, useFlowContainerContext } from '@/contexts/FlowContext'

import { deleteNode } from '../utils/deleteNode'

const nodeModalState = new Set<FlowModalType>(['NODE_CONFIGURE', 'NODE_RUNNING_RESULT'])

export const useDeleteNodeAndEdge = () => {
    const { setModalState, setIsChanged, log, modalState, disable } = useFlowContainerContext()
    const readonly = !!log
    const { setNodes, setEdges } = useFlow()
    const { getNodes, getEdges, updateNode } = useReactFlow()

    const selector = useCallback(
        (store: ReactFlowState) => ({
            selectedNode: store.nodes.find(node => node.selected),
            selectedEdge: store.edges.find(edge => edge.selected)
        }),
        []
    )

    const { selectedNode, selectedEdge } = useStore(selector)
    useUpdateEffect(() => {
        if (selectedNode?.id) {
            setModalState(draft => {
                draft.selectedNodeId = selectedNode?.id
                draft.type = readonly ? 'NODE_RUNNING_RESULT' : 'NODE_CONFIGURE'
            })
            return
        }
        if (modalState.selectedNodeId) {
            setModalState({})
        }
    }, [readonly, selectedNode?.id, setModalState])

    useUpdateEffect(() => {
        if (modalState.type && !nodeModalState.has(modalState.type) && selectedNode?.id) {
            updateNode(selectedNode.id, draft => {
                draft.selected = false
                return draft
            })
        }
    }, [modalState?.type])

    const handleDeleteNode = useCallback(
        (node: Node) => {
            const { id, type } = node
            if (type === ClassNode.TRIGGER || type === ClassNode.END) {
                Toast.info('开始节点和结束节点不能被删除')
                return
            }
            const nodes = getNodes() as FlowNode[]
            const edges = getEdges() as FlowEdge[]
            const { nodes: newNodes, edges: newEdges } = deleteNode(id, nodes, edges)
            setNodes?.(newNodes)
            setEdges?.(newEdges)
            setIsChanged?.(true)
        },
        [getEdges, getNodes, setEdges, setIsChanged, setNodes]
    )

    const handleDeleteEdge = useCallback(
        (id: string) => {
            setEdges?.(draft => draft.filter(edge => edge.id !== id))
            setIsChanged?.(true)
        },
        [setEdges, setIsChanged]
    )

    // const handleClearEdgesSelected = useCallback(() => {
    //     setEdges?.(draft => produce(draft, edges => {
    //         edges.forEach(edge => {
    //             edge.selected = false
    //         })
    //     }))
    // }, [setEdges])

    // const handleNodeClick = useCallback(
    //     (nodeId: string, cancelSelection?: boolean) => {
    //         if (!cancelSelection && selectedNode?.id === nodeId) {
    //             return
    //         }
    //         setNodes?.(nodes => {
    //             return produce(nodes, draft => {
    //                 draft.forEach(node => {
    //                     if (node.id === nodeId) {
    //                         node.selected = !cancelSelection
    //                     } else {
    //                         node.selected = false
    //                     }
    //                 })
    //             })
    //         })
    //         handleClearEdgesSelected()
    //     },
    //     [handleClearEdgesSelected, selectedNode?.id, setNodes]
    // )

    // const handleEdgeClick = useCallback((edgeId: string) => {
    //     setEdges?.(edges => {
    //         return produce(edges, draft => {
    //             draft.forEach(edge => {
    //                 if (edge.id === edgeId) {
    //                     edge.selected = true
    //                 } else {
    //                     edge.selected = false
    //                 }
    //             })
    //         })
    //     })
    // }, [setEdges])

    // const handleClickPanel = useCallback(() => {
    //     if (selectedEdge) {
    //         handleClearEdgesSelected()
    //     }
    // }, [handleClearEdgesSelected, selectedEdge])

    useHotkeys(
        'delete, backspace',
        () => {
            if (disable) {
                return
            }
            if (selectedEdge) {
                handleDeleteEdge(selectedEdge.id)
                return
            }
            if (selectedNode) {
                handleDeleteNode(selectedNode)
            }
        },
        [handleDeleteEdge, handleDeleteNode, selectedNode, selectedEdge, disable]
    )
}
